import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Skull, Shield, Zap } from 'lucide-react';
import { getApiBaseUrl } from '../utils/apiUrl';

const TypeEffectivenessChart = ({ pokemonId, pokemonName }) => {
  const [effectiveness, setEffectiveness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTypeEffectiveness();
  }, [pokemonId]);

  const fetchTypeEffectiveness = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${getApiBaseUrl()}/api/type-effectiveness/pokemon/${pokemonId}`);
      const data = await response.json();

      if (data.success) {
        setEffectiveness(data.data);
      } else {
        setError(data.error || 'ไม่พบข้อมูลความสัมพันธ์ธาตุ');
      }
    } catch (error) {
      console.error('Error fetching type effectiveness:', error);
      setError('เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  }, [pokemonId]);

  const getTypeColor = (type) => {
    const typeColors = {
      'Normal': 'bg-gray-500',
      'Fire': 'bg-red-500',
      'Water': 'bg-blue-500',
      'Electric': 'bg-yellow-500',
      'Grass': 'bg-green-500',
      'Ice': 'bg-cyan-400',
      'Fighting': 'bg-red-600',
      'Poison': 'bg-purple-500',
      'Ground': 'bg-yellow-600',
      'Flying': 'bg-indigo-400',
      'Psychic': 'bg-pink-500',
      'Bug': 'bg-lime-500',
      'Rock': 'bg-yellow-700',
      'Ghost': 'bg-purple-700',
      'Dragon': 'bg-indigo-600',
      'Dark': 'bg-gray-800',
      'Steel': 'bg-gray-400',
      'Fairy': 'bg-pink-300'
    };
    return typeColors[type] || 'bg-gray-500';
  };

  // const getEffectivenessColor = (multiplier) => {
  //   if (multiplier > 1.5) return 'bg-red-600';
  //   if (multiplier > 1) return 'bg-red-500';
  //   if (multiplier < 0.5) return 'bg-green-600';
  //   if (multiplier < 1) return 'bg-green-500';
  //   return 'bg-gray-500';
  // };

  // const getEffectivenessText = (multiplier) => {
  //   if (multiplier > 1.5) return 'Super Effective';
  //   if (multiplier > 1) return 'Effective';
  //   if (multiplier < 0.5) return 'Not Very Effective';
  //   if (multiplier < 1) return 'Resistant';
  //   return 'Normal';
  // };

  if (loading) {
    return (
      <div className="bg-white rounded-3xl p-8 shadow-pokemon">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">กำลังโหลดข้อมูลความสัมพันธ์ธาตุ...</span>
        </div>
      </div>
    );
  }

  // Don't show component if there's an error or no data
  if (error || !effectiveness) {
    return null;
  }

  const { increasedDamage, reducedDamage } = effectiveness.effectiveness || {};
  
  // Filter out 0% damage and group by percentage
  const filteredIncreasedDamage = (increasedDamage || []).filter(damage => parseFloat(damage.percentage) > 0);
  const filteredReducedDamage = (reducedDamage || []).filter(damage => parseFloat(damage.percentage) < 0);
  
  // Group by percentage for better display
  const groupedIncreasedDamage = filteredIncreasedDamage.reduce((groups, damage) => {
    const percentage = damage.percentage;
    if (!groups[percentage]) {
      groups[percentage] = [];
    }
    groups[percentage].push(damage);
    return groups;
  }, {});
  
  const groupedReducedDamage = filteredReducedDamage.reduce((groups, damage) => {
    const percentage = damage.percentage;
    if (!groups[percentage]) {
      groups[percentage] = [];
    }
    groups[percentage].push(damage);
    return groups;
  }, {});

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
      className="bg-white rounded-3xl p-8 shadow-pokemon"
    >
      <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
        Type Effectiveness Chart
      </h3>
      
      {/* Increased Damage Section */}
      {Object.keys(groupedIncreasedDamage).length > 0 && (
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <div className="bg-red-500 text-white px-4 py-2 rounded-lg flex items-center">
              <Skull className="w-5 h-5 mr-2" />
              <span className="font-semibold">
                {pokemonName} takes increased damage from:
              </span>
            </div>
          </div>
          
          <div className="space-y-3">
            {Object.entries(groupedIncreasedDamage).map(([percentage, damages], groupIndex) => (
              <motion.div
                key={percentage}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: groupIndex * 0.1 }}
                className="flex items-center space-x-3"
              >
                <div className="bg-red-500 text-white px-3 py-1 rounded-lg font-semibold min-w-[80px] text-center">
                  +{percentage}%
                </div>
                <div className="flex flex-wrap gap-2">
                  {damages.map((damage, index) => (
                    <div key={damage.type} className={`${getTypeColor(damage.type)} text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2`}>
                      <img 
                        src={`/images/Types/${damage.type}.webp`} 
                        alt={damage.type}
                        className="w-5 h-5"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <span>{damage.type}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Reduced Damage Section */}
      {Object.keys(groupedReducedDamage).length > 0 && (
        <div>
          <div className="flex items-center mb-4">
            <div className="bg-green-500 text-white px-4 py-2 rounded-lg flex items-center">
              <Shield className="w-5 h-5 mr-2" />
              <span className="font-semibold">
                {pokemonName} takes reduced damage from:
              </span>
            </div>
          </div>
          
          <div className="space-y-3">
            {Object.entries(groupedReducedDamage).map(([percentage, damages], groupIndex) => (
              <motion.div
                key={percentage}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: groupIndex * 0.1 }}
                className="flex items-center space-x-3"
              >
                <div className="bg-green-500 text-white px-3 py-1 rounded-lg font-semibold min-w-[80px] text-center">
                  {percentage}%
                </div>
                <div className="flex flex-wrap gap-2">
                  {damages.map((damage, index) => (
                    <div key={damage.type} className={`${getTypeColor(damage.type)} text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2`}>
                      <img 
                        src={`/images/Types/${damage.type}.webp`} 
                        alt={damage.type}
                        className="w-5 h-5"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <span>{damage.type}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Pokemon Types */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-center space-x-2">
          <Zap className="w-5 h-5 text-blue-500" />
          <span className="text-gray-600 font-medium">Pokemon Types:</span>
          {effectiveness.pokemonTypes && effectiveness.pokemonTypes.map((type, index) => (
            <div key={type} className={`${getTypeColor(type)} text-white px-3 py-1 rounded-lg font-medium flex items-center space-x-2`}>
              <img 
                src={`/images/Types/${type}.webp`} 
                alt={type}
                className="w-5 h-5"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <span>{type}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default TypeEffectivenessChart;
